import type {
  ActivityType,
  IndustryVertical,
  PlanType,
  PostStatus,
  SocialPlatform,
} from '@app/common';
import type { Language } from '../constants/language';
import { en } from './en';
import { bn } from './bn';

/** Every key the app can translate, derived from the English dictionary. */
export type TranslationKey = keyof typeof en;

/**
 * Plan names for display. `PLAN_NAMES` in `@app/common` is shared with the backend
 * and is English-only by design, so the UI resolves its own labels — reusing the
 * pricing keys rather than defining a second set that could drift from them.
 */
export const PLAN_NAME_KEYS: Record<PlanType, TranslationKey> = {
  free_trial: 'pricing.planFreeTrial',
  starter: 'pricing.planStarter',
  growth: 'pricing.planGrowth',
  agency: 'pricing.planAgency',
  institution: 'pricing.planEnterprise',
  // Retired plans. Still labelled because existing subscribers are still on them
  // and their payment history still lists them.
  pro_trial: 'pricing.planLegacyProTrial',
  pro: 'pricing.planLegacyPro',
};

/**
 * Human labels for audit-log event types. Exhaustive by type, so adding a value
 * to `ActivityType` in `@app/common` is a compile error until it is labelled in
 * both languages — the same guarantee `bn.ts` gives for keys.
 */
export const ACTIVITY_TYPE_KEYS: Record<ActivityType, TranslationKey> = {
  user_signed_up: 'activity.user_signed_up',
  user_signed_in: 'activity.user_signed_in',
  content_generated: 'activity.content_generated',
  generation_blocked: 'activity.generation_blocked',
  generation_failed: 'activity.generation_failed',
  brand_created: 'activity.brand_created',
  account_connected: 'activity.account_connected',
  account_disconnected: 'activity.account_disconnected',
  account_reauth_required: 'activity.account_reauth_required',
  post_scheduled: 'activity.post_scheduled',
  post_published: 'activity.post_published',
  post_failed: 'activity.post_failed',
  post_approved: 'activity.post_approved',
  post_rejected: 'activity.post_rejected',
  token_refresh_failed: 'activity.token_refresh_failed',
  payment_initiated: 'activity.payment_initiated',
  payment_completed: 'activity.payment_completed',
  payment_failed: 'activity.payment_failed',
  subscription_activated: 'activity.subscription_activated',
  subscription_cancelled: 'activity.subscription_cancelled',
  user_banned: 'activity.user_banned',
  user_unbanned: 'activity.user_unbanned',
  tenant_created: 'activity.tenant_created',
  tenant_deleted: 'activity.tenant_deleted',
  tenant_plan_changed: 'activity.tenant_plan_changed',
  tenant_admin_changed: 'activity.tenant_admin_changed',
  member_invited: 'activity.member_invited',
  member_joined: 'activity.member_joined',
  member_removed: 'activity.member_removed',
  member_role_changed: 'activity.member_role_changed',
  permission_granted: 'activity.permission_granted',
  permission_revoked: 'activity.permission_revoked',
  team_created: 'activity.team_created',
  team_deleted: 'activity.team_deleted',
  team_membership_changed: 'activity.team_membership_changed',
  team_visibility_changed: 'activity.team_visibility_changed',
  question_generated: 'activity.question_generated',
};

/**
 * Platform names are proper nouns and identical in both dictionaries, but they
 * still resolve through keys so the admin feed and composer cannot drift, and so
 * a new platform fails the build until it is named.
 */
export const PLATFORM_NAME_KEYS: Record<SocialPlatform, TranslationKey> = {
  facebook: 'platform.facebook',
  instagram: 'platform.instagram',
};

export const VERTICAL_NAME_KEYS: Record<IndustryVertical, TranslationKey> = {
  restaurant: 'vertical.restaurant',
  dental: 'vertical.dental',
  real_estate: 'vertical.real_estate',
  cleaning: 'vertical.cleaning',
  agency: 'vertical.agency',
  other: 'vertical.other',
};

export const POST_STATUS_KEYS: Record<PostStatus, TranslationKey> = {
  draft: 'postStatus.draft',
  pending_review: 'postStatus.pending_review',
  approved: 'postStatus.approved',
  scheduled: 'postStatus.scheduled',
  publishing: 'postStatus.publishing',
  published: 'postStatus.published',
  failed: 'postStatus.failed',
  cancelled: 'postStatus.cancelled',
};

/** Values substituted into `{placeholder}` slots. */
export type TranslationParams = Record<string, string | number>;

/** The shape `useTranslation().t` has, for helpers that take it as an argument. */
export type TranslateFn = (key: TranslationKey, params?: TranslationParams) => string;

const DICTIONARIES: Record<Language, Record<TranslationKey, string>> = { en, bn };

/**
 * Resolves a key in the given language, substituting any `{placeholder}` slots.
 *
 * There is no missing-key fallback because there cannot be a missing key: `bn` is
 * typed against `TranslationKey`, so an incomplete dictionary fails the build.
 */
export function translate(
  language: Language,
  key: TranslationKey,
  params?: TranslationParams
): string {
  const template = DICTIONARIES[language][key];
  if (!params) return template;

  return Object.entries(params).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(String(value)),
    template
  );
}

export { en, bn };
